import { isExcludedClient } from '~/config/playerLink.config';

/**
 * Thème de saison sur la console des bars (Halloween 2026, décision Victorien 14/10) :
 * menu de la TV (couleurs, décor, annonce de la soirée, pastilles sur les jeux qui ont
 * un contenu de saison) et écran « entre deux jeux » du téléphone des joueurs.
 *
 * - S'allume et s'éteint SEUL par date (pas de retrait manuel) : la TV se réévalue pile
 *   aux bornes, même ouverte depuis des heures (useSeasonalTheme).
 * - Jamais en aéroport ni chez les clients sous contrat (même liste que le lien joueurs),
 *   jamais en mode auto. Le compte de démo publique le voit (prospects).
 * - Interrupteurs : `clients/{id}.features.seasonalTheme === false` coupe un lieu en direct
 *   (script pilotage `theme_saison.py`, tout le parc d'un coup) ; `SEASONAL_THEME_ENABLED`
 *   coupe tout au prochain chargement de la console.
 * - QR et barre du haut jamais touchés.
 */
export const SEASONAL_THEME_ENABLED = true;

export type SeasonalThemeId = 'halloween';

/** Champ `features.<flag>` du doc client : `false` = thème coupé pour ce lieu. */
export const SEASONAL_THEME_FLAG = 'seasonalTheme';

/** Propriété partagée diffusée par la TV aux téléphones (valeur : id du thème ou null). */
export const SEASONAL_SHARED_KEY = 'seasonalTheme';

/** Aperçu avant la date (vérification) : `localStorage.unboared_seasonal_preview = 'halloween'`. */
export const SEASONAL_PREVIEW_KEY = 'unboared_seasonal_preview';

type SeasonWindow = { id: SeasonalThemeId; start: number; end: number };

/** Jeu. 22/10/2026 06:00 (Paris, heure d'été) → lun. 02/11/2026 06:00 (Paris, heure d'hiver). */
export const SEASON_WINDOWS: SeasonWindow[] = [
    { id: 'halloween', start: Date.parse('2026-10-22T06:00:00+02:00'), end: Date.parse('2026-11-02T06:00:00+01:00') },
];

/** Jeux qui ont un contenu de saison (pastille + mise en tête). L'ordre = l'ordre affiché. */
export const SEASONAL_GAMES: Record<SeasonalThemeId, string[]> = {
    halloween: ['unblind-test', 'unquiz', 'draw-guessr'],
};

export function seasonAt(now: number = Date.now()): SeasonalThemeId | null {
    return SEASON_WINDOWS.find((w) => now >= w.start && now < w.end)?.id ?? null;
}

/** Prochain instant où `seasonAt` change de valeur (null si plus rien ne change). */
export function nextSeasonBoundary(now: number = Date.now()): number | null {
    const next = SEASON_WINDOWS.flatMap((w) => [w.start, w.end]).filter((t) => t > now);
    return next.length ? Math.min(...next) : null;
}

/** Fin de la saison `id` (le téléphone ne garde jamais le thème au-delà). */
export function seasonEnd(id: SeasonalThemeId): number {
    return SEASON_WINDOWS.find((w) => w.id === id)?.end ?? 0;
}

/** Bars et animateurs seulement : ni mode auto, ni aéroport, ni client sous contrat. Démo publique incluse. */
export function isSeasonalClient(clientID: string | undefined, mode: string | undefined): boolean {
    if (!SEASONAL_THEME_ENABLED || !clientID || mode === 'auto') return false;
    return clientID === 'demo' || !isExcludedClient(clientID);
}
